import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { Menu, ShoppingBag, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useGlassSheen } from '@/hooks/use-glass-sheen'
import { AvelineLogo } from '@/components/generals/aveline-logo'
import { useBag } from '@/components/generals/bag-provider'
import { NAV_LINKS } from '@/constants/landing'

export const SiteHeader: FC = (): ReactNode => {
    const [scrolled, setScrolled] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const { count, openBag } = useBag()
    const sheen = useGlassSheen<HTMLElement>()

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 24)

        handleScroll()
        window.addEventListener('scroll', handleScroll, { passive: true })

        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    useEffect(() => {
        if (!menuOpen) return

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setMenuOpen(false)
        }

        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [menuOpen])

    const closeMenu = () => setMenuOpen(false)

    return (
        <header className='fixed inset-x-0 top-0 z-50 px-3 pt-3 sm:px-6 sm:pt-5'>
            <nav
                ref={sheen.ref}
                onPointerMove={sheen.onPointerMove}
                onPointerLeave={sheen.onPointerLeave}
                aria-label="Main"
                className={cn(
                    'glass mx-auto flex h-16 max-w-6xl items-center justify-between rounded-full pl-5 pr-2.5 text-espresso transition-shadow duration-500 sm:pl-7',
                    (scrolled || menuOpen) && 'glass-frost'
                )}
            >
                <a
                    href='#hero'
                    onClick={closeMenu}
                    aria-label="Maison Aveline, back to the top"
                    className='rounded-full focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold-deep'
                >
                    <AvelineLogo />
                </a>

                <ul className='hidden items-center gap-8 lg:flex'>
                    {NAV_LINKS.map((link) => (
                        <li key={link.href}>
                            <a
                                href={link.href}
                                className='relative text-[0.8rem] uppercase tracking-[0.2em] text-espresso/75 transition-colors duration-300 after:absolute after:inset-x-0 after:-bottom-1.5 after:h-px after:origin-left after:scale-x-0 after:bg-gold-deep after:transition-transform after:duration-500 hover:text-espresso hover:after:scale-x-100 focus-visible:text-espresso focus-visible:outline-none focus-visible:after:scale-x-100'
                            >
                                {link.label}
                            </a>
                        </li>
                    ))}
                </ul>

                <div className='flex items-center gap-2'>
                    <a
                        href='#cercle'
                        className='hidden h-11 items-center rounded-full bg-espresso px-5 text-[0.78rem] uppercase tracking-[0.18em] text-ivory transition-colors duration-300 hover:bg-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-deep sm:inline-flex'
                    >
                        Join Le Cercle
                    </a>

                    <button
                        type='button'
                        onClick={() => {
                            closeMenu()
                            openBag('bag')
                        }}
                        aria-label={count > 0 ? `Open your bag, ${count} ${count === 1 ? 'piece' : 'pieces'}` : "Open your bag"}
                        className='relative flex size-11 items-center justify-center rounded-full bg-white/50 ring-1 ring-espresso/10 transition-colors duration-300 hover:bg-white/80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-deep'
                    >
                        <ShoppingBag className='size-5' strokeWidth={1.5} />

                        {count > 0 && (
                            <span className='absolute -right-0.5 -top-0.5 flex size-5 items-center justify-center rounded-full bg-gold-deep text-[0.65rem] font-medium text-ivory animate-in zoom-in duration-300'>
                                {count}
                            </span>
                        )}
                    </button>

                    <button
                        type='button'
                        onClick={() => setMenuOpen((open) => !open)}
                        aria-expanded={menuOpen}
                        aria-controls='mobile-menu'
                        aria-label={menuOpen ? "Close the menu" : "Open the menu"}
                        className='flex size-11 items-center justify-center rounded-full bg-white/50 ring-1 ring-espresso/10 transition-colors duration-300 hover:bg-white/80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-deep lg:hidden'
                    >
                        {menuOpen ? <X className='size-5' strokeWidth={1.5} /> : <Menu className='size-5' strokeWidth={1.5} />}
                    </button>
                </div>
            </nav>

            <div
                id='mobile-menu'
                hidden={!menuOpen}
                className='glass glass-frost mx-auto mt-2 max-w-6xl rounded-[2rem] p-3 text-espresso animate-in fade-in slide-in-from-top-2 duration-300 lg:hidden'
            >
                <ul className='flex flex-col'>
                    {NAV_LINKS.map((link) => (
                        <li key={link.href}>
                            <a
                                href={link.href}
                                onClick={closeMenu}
                                className='flex items-center justify-between rounded-2xl px-4 py-3.5 font-heading text-2xl transition-colors hover:bg-white/60 focus-visible:bg-white/60 focus-visible:outline-none'
                            >
                                {link.label}
                                <span aria-hidden='true' className='size-1.5 rotate-45 bg-gold-deep/60' />
                            </a>
                        </li>
                    ))}
                </ul>

                <a
                    href='#cercle'
                    onClick={closeMenu}
                    className='mt-2 flex h-12 items-center justify-center rounded-full bg-espresso text-[0.8rem] uppercase tracking-[0.18em] text-ivory sm:hidden'
                >
                    Join Le Cercle
                </a>
            </div>
        </header>
    )
}
